import './update-proposed-tenant.scss'

import React, { useEffect, useState } from 'react'
import { notification, Tabs, Drawer } from 'antd'
import { FormikHelpers } from 'formik'
import useSWR from 'swr'

import { httpClient } from '../../../../util/http-client'
import { onboardingFetcher } from '../../../../api'
import { parseApiErrorResponse } from '../../../../components/forms/utils'

import { messages } from '../../messages'
import { drawerAndModalWidth } from '../../../../util/guideline'
import { TenantFormValues, updateTenantInitialData } from '../../models'
import { InformationPanel } from './InformationPanel/InformationPanel'
import { UsersPanel } from './UsersPanel/UsersPanel'
import { IssuersPanel } from './IssuersPanel/IssuersPanel'

const { TabPane } = Tabs

interface Props {
  tenantId: number
  isVisible: boolean
  onSuccess: () => void
  onClose: () => void
}

export const UpdateProposedTenantDrawer = ({ tenantId, onSuccess, onClose, isVisible }: Props) => {
  const [initialValues, setInitialValues] = useState<TenantFormValues>(updateTenantInitialData)

  const tenantDetailUrl = `/proposed-tenants/${tenantId}/`
  const tenantRejectUrl = `/proposed-tenants/${tenantId}/reject/`

  const { data: tenant } = useSWR(tenantId ? tenantDetailUrl : null, onboardingFetcher)

  const onSubmit = async (
    values: TenantFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<TenantFormValues>,
  ) => {
    try {
      await httpClient(tenantDetailUrl, { onboardingService: true, method: 'PUT', data: values })
      notification.success({ message: messages.common.success, description: messages.proposedTenant.update.success })
      resetForm()
      onSuccess()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)

      notification.error({ message: messages.common.error, description: messages.proposedTenant.update.error })
      return
    } finally {
      setSubmitting(false)
    }
  }

  const onReject = async () => {
    try {
      await httpClient(tenantRejectUrl, { onboardingService: true, method: 'DELETE' })
      notification.success({ message: messages.common.success, description: messages.proposedTenant.update.success })
      onSuccess()
    } catch (error) {
      notification.error({ message: messages.common.error, description: messages.proposedTenant.update.error })
    }
  }

  useEffect(() => {
    if (tenant) {
      setInitialValues(tenant)
    }
  }, [tenant])

  return (
    <Drawer
      title={`${tenant?.acceptanceStatus === 'ACCEPTED' ? 'View' : 'Update'} Customer`}
      className="update-proposed-tenant"
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={drawerAndModalWidth}
      destroyOnClose
    >
      <Tabs defaultActiveKey="information" size="large">
        <TabPane tab="Information" key="information">
          <InformationPanel tenant={tenant} initialValues={initialValues} onReject={onReject} onSubmit={onSubmit} />
        </TabPane>
        <TabPane tab="Users" key="users">
          <UsersPanel tenant={tenant} />
        </TabPane>
        <TabPane tab="Banks" key="issuers">
          <IssuersPanel tenant={tenant} />
        </TabPane>
      </Tabs>
    </Drawer>
  )
}
